import { Link, router, usePage } from '@inertiajs/react'
import { LogOut, Settings, FileCode, Shield } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu'
import type { CurrentUser, SharedProps } from '@/types/shared'

type ProfileUser = Pick<CurrentUser, 'id' | 'email' | 'full_name' | 'initials' | 'avatar_url' | 'super_admin'>

interface ProfileDropdownProps {
  user: ProfileUser
}

export function ProfileDropdown({ user }: ProfileDropdownProps) {
  const { impersonating } = usePage<SharedProps>().props

  const handleLogout = () => {
    router.delete('/logout', {
      preserveState: false,
      preserveScroll: false,
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="cursor-pointer rounded-full p-0.5 border-2 border-border hover:border-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors">
          <div className="size-8 rounded-full bg-primary/20 flex items-center justify-center">
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.full_name}
                className="size-8 rounded-full object-cover"
              />
            ) : (
              <span className="text-xs font-medium text-primary">{user.initials}</span>
            )}
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user.full_name}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/*
          asChild + Inertia Link gives us a real <a href> so right-click /
          middle-click / cmd-click all work. onSelect e.preventDefault() stops
          Radix from auto-closing the menu and unmounting the Slot child before
          the Link's click handler fires — that race was why the original
          version did nothing on plain left-click.
        */}
        <DropdownMenuItem
          asChild
          className="cursor-pointer"
          onSelect={(e) => e.preventDefault()}
        >
          <Link href="/profile" className="flex items-center">
            <Settings className="mr-2 h-4 w-4 text-foreground" />
            <span>Profile</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          asChild
          className="cursor-pointer"
          onSelect={(e) => e.preventDefault()}
        >
          <a href="/docs" className="flex items-center">
            <FileCode className="mr-2 h-4 w-4 text-foreground" />
            <span>API Docs</span>
          </a>
        </DropdownMenuItem>
        {user.super_admin && !impersonating && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => router.post('/god-mode/enter')}
              className="cursor-pointer text-amber-600 dark:text-amber-400"
            >
              <Shield className="mr-2 h-4 w-4" />
              <span>God Mode</span>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          className="dropdown-item-destructive cursor-pointer text-red-600 dark:text-red-400"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
